const APP_UPDATE_EVENT = "elsa-app-update-available";
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_FOCUS_CHECK_DELAY_MS = 30 * 1000;

let currentBuildSignature = null;
let currentVersionMetadata = null;
let lastServerBuildSignature = null;
let lastServerVersionMetadata = null;
let hasNotifiedUpdate = false;
let pendingServiceWorkerRegistration = null;
let latestUpdateDetails = null;
let pollingTimer = null;
let isPollingStarted = false;
let lastFocusCheckAt = 0;
export let stopManifestUpdatePolling = () => {};

function buildPublicUrl(path) {
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${path}`;
}

function normalizeAssetPath(assetUrl) {
  try {
    return new URL(assetUrl, window.location.origin).pathname;
  } catch {
    return null;
  }
}

function isBuildAssetPath(assetPath) {
  return Boolean(
    assetPath &&
      assetPath.includes("/static/") &&
      /\.(js|css)$/i.test(assetPath)
  );
}

function createSignature(paths) {
  const normalizedPaths = Array.from(new Set(paths.filter(Boolean))).sort();
  return normalizedPaths.length > 0 ? normalizedPaths.join("|") : null;
}

function getLoadedBuildSignature() {
  if (typeof document === "undefined") {
    return null;
  }

  const assetPaths = [];

  document.querySelectorAll("script[src]").forEach((scriptElement) => {
    const path = normalizeAssetPath(scriptElement.getAttribute("src"));
    if (isBuildAssetPath(path)) {
      assetPaths.push(path);
    }
  });

  document
    .querySelectorAll('link[rel="stylesheet"][href]')
    .forEach((linkElement) => {
      const path = normalizeAssetPath(linkElement.getAttribute("href"));
      if (isBuildAssetPath(path)) {
        assetPaths.push(path);
      }
    });

  return createSignature(assetPaths);
}

function getManifestBuildSignature(manifest) {
  const entrypoints = Array.isArray(manifest?.entrypoints)
    ? manifest.entrypoints
    : [];

  const entrypointPaths = entrypoints
    .map((entrypoint) => normalizeAssetPath(entrypoint))
    .filter(isBuildAssetPath);

  return createSignature(entrypointPaths);
}

async function fetchManifestBuildSignature() {
  const response = await fetch(buildPublicUrl("/asset-manifest.json"), {
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      "Cache-Control": "no-cache",
    },
  });

  if (!response.ok) {
    return null;
  }

  const manifest = await response.json();
  return getManifestBuildSignature(manifest);
}

async function fetchVersionMetadata() {
  const response = await fetch(buildPublicUrl("/version.json"), {
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      "Cache-Control": "no-cache",
    },
  });

  if (!response.ok) {
    return null;
  }

  const metadata = await response.json();
  return metadata && metadata.buildId ? metadata : null;
}

function hasVersionMetadataChanged(serverVersionMetadata) {
  if (!serverVersionMetadata?.buildId) {
    return false;
  }

  if (!currentVersionMetadata) {
    currentVersionMetadata = serverVersionMetadata;
    return false;
  }

  return serverVersionMetadata.buildId !== currentVersionMetadata.buildId;
}

function dispatchUpdateAvailable(details) {
  if (typeof window === "undefined") {
    return;
  }

  latestUpdateDetails = {
    availableAt: Date.now(),
    source: "manifest",
    ...details,
  };

  hasNotifiedUpdate = true;
  window.dispatchEvent(
    new CustomEvent(APP_UPDATE_EVENT, {
      detail: latestUpdateDetails,
    })
  );
}

export function notifyServiceWorkerUpdateAvailable(registration) {
  pendingServiceWorkerRegistration = registration || null;

  if (hasNotifiedUpdate) {
    return;
  }

  dispatchUpdateAvailable({
    source: "service-worker",
  });
}

export function subscribeToAppUpdates(listener) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleUpdate = (event) => {
    listener(event.detail);
  };

  window.addEventListener(APP_UPDATE_EVENT, handleUpdate);

  if (latestUpdateDetails) {
    window.setTimeout(() => listener(latestUpdateDetails), 0);
  }

  return () => {
    window.removeEventListener(APP_UPDATE_EVENT, handleUpdate);
  };
}

export async function checkForAppUpdate() {
  if (process.env.NODE_ENV !== "production" || typeof window === "undefined") {
    return null;
  }

  if (!currentBuildSignature) {
    currentBuildSignature = getLoadedBuildSignature();
  }

  try {
    const [serverBuildSignature, serverVersionMetadata] = await Promise.all([
      fetchManifestBuildSignature().catch(() => null),
      fetchVersionMetadata().catch(() => null),
    ]);

    if (serverVersionMetadata) {
      lastServerVersionMetadata = serverVersionMetadata;
    }

    const versionMetadataChanged =
      hasVersionMetadataChanged(serverVersionMetadata);

    if (!serverBuildSignature) {
      if (
        versionMetadataChanged &&
        !hasNotifiedUpdate
      ) {
        dispatchUpdateAvailable({
          source: "version",
          serverVersion: serverVersionMetadata,
          currentVersion: currentVersionMetadata,
        });
      }

      return serverVersionMetadata;
    }

    lastServerBuildSignature = serverBuildSignature;

    if (!currentBuildSignature) {
      currentBuildSignature = serverBuildSignature;
      return null;
    }

    if (
      (serverBuildSignature !== currentBuildSignature ||
        versionMetadataChanged) &&
      !hasNotifiedUpdate
    ) {
      dispatchUpdateAvailable({
        source: "manifest",
        currentBuildSignature,
        serverBuildSignature,
        currentVersion: currentVersionMetadata,
        serverVersion: serverVersionMetadata,
      });
    }

    return serverBuildSignature;
  } catch (error) {
    console.warn("[AppUpdate] Echec verification version:", error);
    return null;
  }
}

export function startManifestUpdatePolling(options = {}) {
  if (process.env.NODE_ENV !== "production" || typeof window === "undefined") {
    return () => {};
  }

  if (isPollingStarted) {
    return stopManifestUpdatePolling;
  }

  const intervalMs = options.intervalMs || DEFAULT_CHECK_INTERVAL_MS;
  isPollingStarted = true;
  currentBuildSignature = getLoadedBuildSignature();

  window.setTimeout(() => {
    void checkForAppUpdate();
  }, 3000);

  pollingTimer = window.setInterval(() => {
    void checkForAppUpdate();
  }, intervalMs);

  const handleOnline = () => {
    void checkForAppUpdate();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void checkForAppUpdate();
    }
  };

  const handleFocus = () => {
    const now = Date.now();
    if (now - lastFocusCheckAt < MIN_FOCUS_CHECK_DELAY_MS) {
      return;
    }

    lastFocusCheckAt = now;
    void checkForAppUpdate();
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("focus", handleFocus);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  stopManifestUpdatePolling = () => {
    if (pollingTimer) {
      window.clearInterval(pollingTimer);
      pollingTimer = null;
    }

    isPollingStarted = false;
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("focus", handleFocus);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  return stopManifestUpdatePolling;
}

function waitForControllerChange(timeoutMs = 4000) {
  if (!("serviceWorker" in navigator)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let resolved = false;

    const cleanup = () => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange
      );
    };

    const finish = () => {
      if (resolved) {
        return;
      }

      resolved = true;
      cleanup();
      resolve();
    };

    const handleControllerChange = () => {
      finish();
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange
    );
    window.setTimeout(finish, timeoutMs);
  });
}

async function activateWaitingServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registration =
    pendingServiceWorkerRegistration ||
    (await navigator.serviceWorker.getRegistration());
  const waitingWorker = registration?.waiting || null;

  if (!waitingWorker) {
    if (registration?.update) {
      await Promise.resolve(registration.update()).catch(() => undefined);
    }
    return;
  }

  const controllerChange = waitForControllerChange();
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
  await controllerChange;
}

async function clearAppShellCaches() {
  if (!("caches" in window)) {
    return;
  }

  const cacheNames = await caches.keys();
  const shellCacheNames = cacheNames.filter(
    (cacheName) =>
      cacheName.startsWith("elsa-") &&
      (cacheName.endsWith("-static") || cacheName.endsWith("-dynamic"))
  );

  await Promise.all(shellCacheNames.map((cacheName) => caches.delete(cacheName)));
}

export async function activateUpdateAndReload() {
  try {
    await activateWaitingServiceWorker();
    await clearAppShellCaches();
  } catch (error) {
    console.warn("[AppUpdate] Activation SW impossible:", error);
  } finally {
    window.location.reload();
  }
}

export function getLastServerBuildSignature() {
  return lastServerBuildSignature;
}

export function getLastServerVersionMetadata() {
  return lastServerVersionMetadata;
}
